import { SipActionFilter, SipPlanMode } from '../../../api/types'
import {
  isSipPlansFetchFailed,
  isSipPlansLoading,
  useSipPlansContext
} from '../../SipPlansProvider'
import type { DerivedYourPlanCategory } from '../../deriveSipPlansView'
import { SipPlansListEmptyState } from '../../sip-ui-components/SipPlansListEmptyState'
import { SipPlansListFilterEmptyState } from '../../sip-ui-components/SipPlansListFilterEmptyState'
import { PlansCategoryListSkeleton } from '../../../../lumpsum/plans-list/lumpsum-ui-components/PlansCategoryListSkeleton'
import { useSipPlanCategoryList } from '../../usePlanCategoryBucketExpansion'
import { YourPlanCategoryCard } from './YourPlanCategoryCard'
import { YourPlanPlansListErrorState } from './YourPlanPlansListErrorState'
import { isSipYourPlanListFullyEmpty } from '../../../../utils/sip/planListUtils'

type YourPlanCategoryListProps = {
  /** When set, render these instead of context and skip query loading/error/empty shells. */
  categories?: DerivedYourPlanCategory[] | null
}

export function YourPlanCategoryList({
  categories: dummyCategories
}: YourPlanCategoryListProps = {}) {
  const { actionFiltersByPlanMode, derivedPlans, plansOverview, plansQuery } = useSipPlansContext()
  const hasDummyCategories = dummyCategories !== undefined
  // Your plan panel stays mounted when Ideal tab is active; don't use active-tab planMode/actionFilter.
  const actionFilter = hasDummyCategories
    ? SipActionFilter.ALL
    : (actionFiltersByPlanMode[SipPlanMode.YOUR_PLAN] ?? SipActionFilter.ALL)
  const sipPlanCategories = hasDummyCategories
    ? (dummyCategories ?? [])
    : derivedPlans?.yourPlanCategories

  const categoryEntries = useSipPlanCategoryList<DerivedYourPlanCategory>(
    sipPlanCategories,
    actionFilter,
    SipPlanMode.YOUR_PLAN
  )

  if (!hasDummyCategories) {
    if (isSipPlansLoading(SipPlanMode.YOUR_PLAN, plansQuery)) {
      return <PlansCategoryListSkeleton />
    }

    if (isSipPlansFetchFailed(SipPlanMode.YOUR_PLAN, plansQuery)) {
      return <YourPlanPlansListErrorState onRetry={plansQuery.refetch} />
    }

    if (isSipYourPlanListFullyEmpty(sipPlanCategories)) {
      return (
        <SipPlansListEmptyState
          planMode={SipPlanMode.YOUR_PLAN}
          planSnapshotDate={plansOverview?.planSnapshotDate}
          planDurationInMonths={plansOverview?.planDurationInMonths}
        />
      )
    }

    if (categoryEntries.length <= 0) {
      return <SipPlansListFilterEmptyState actionFilter={actionFilter} />
    }
  }

  return (
    <>
      <YourPlanCategoryListStickyBar categoryCount={categoryEntries.length} />
      {categoryEntries.map(({ category, categoryIndex }, entryIndex) => (
        <YourPlanCategoryCard
          key={category?.categoryId ?? `category-${categoryIndex}`}
          category={category}
          categoryIndex={categoryIndex}
          actionFilter={actionFilter}
          noDivider={entryIndex === 0}
        />
      ))}
    </>
  )
}

export function YourPlanCategoryListStickyBar({ categoryCount }: { categoryCount: number }) {
  return (
    <div className="sticky top-[-2px] z-10 flex items-center justify-between bg-white px-5 py-4 border-b border-[#E8EBEA]">
      <div className="text-[13px] font-[500] leading-[150%] text-[#232323]">
        {categoryCount} {categoryCount === 1 ? 'Category' : 'Categories'}
      </div>
      <div className="text-[13px] font-[500] leading-[150%] text-[#232323]">Actions</div>
    </div>
  )
}
